import { logoutLocal } from "@services/auth";
import { toast } from "react-toastify";
import { isIdleTimedOut } from "@utils/sessionActivity";
import { canPreviewOtherVerticals } from "@utils/userUtils";
import { getRequestVerticalOverride } from "@services/verticals";
import { getAppPath, isPublicPath } from "@utils/appPath";
/**
 * Fired when Rules returns HTTP 402 with LICENSE_INACTIVE so the dashboard can
 * show the full-page license inactive UI even if the user was already inside the app.
 */
export const RULES_LICENSE_INACTIVE_EVENT = "unicomply-rules-license-inactive";

/** Fired when facility association access is revoked (HTTP 403 access-removed codes). */
export const FACILITY_ACCESS_REMOVED_EVENT = "unicomply-facility-access-removed";

/**
 * Module-level flag to prevent multiple concurrent redirects to /login
 * when several API calls fail with 401 simultaneously.
 */
let isRedirecting = false;

type UnauthorizedReason = "idle" | "missing_token" | "unauthorized";

/**
 * Centralized token validation. Returns the current access token if it
 * exists and has not expired, or null otherwise.
 *
 * Replaces the duplicated getValidAccessToken() found across service files.
 */
export function getValidAccessToken(): string | null {
  const token = localStorage.getItem("access_token");
  if (!token) return null;
  if (isIdleTimedOut()) return null;
  // Do not send the JWT after the server-reported expiry (must match JWT ``exp``).
  const expStr = localStorage.getItem("access_token_expires_at");
  if (expStr) {
    const expMs = Number(expStr);
    if (Number.isFinite(expMs) && Date.now() >= expMs) return null;
  }
  return token;
}

/**
 * Clears session data and redirects to /login.
 * Uses a module-level guard so only the first caller triggers the redirect.
 * Shows a toast so the user understands why they are being redirected.
 */
function handleUnauthorized(reason: UnauthorizedReason = "unauthorized"): void {
  if (isRedirecting) return;

  // Public routes should never force a session-expired toast/redirect.
  // They are intentionally accessible without authentication.
  const currentPath = getAppPath();
  if (isPublicPath(currentPath)) {
    logoutLocal().catch(() => {});
    return;
  }

  // A 401 from the server does not always mean the session has expired.
  // If the token is still within its issued validity window, the 401 is a
  // backend permission/role issue for that specific endpoint — not a session
  // problem. Skip the logout redirect so the user is not kicked out of a
  // perfectly valid session.
  if (reason === "unauthorized") {
    const expiresAt = localStorage.getItem("access_token_expires_at");
    if (expiresAt) {
      const expiresAtMs = Number(expiresAt);
      if (Number.isFinite(expiresAtMs) && Date.now() < expiresAtMs) {
        return;
      }
    }
  }

  isRedirecting = true;

  try {
    logoutLocal();
  } catch (_) {
    // Best-effort cleanup
  }

  const message =
    reason === "idle"
      ? "You were signed out due to inactivity. Please sign in again."
      : "Your session has expired. Please sign in again.";

  toast.error(message, {
    autoClose: 2000,
    toastId: "session-expired",
  });

  setTimeout(() => {
    window.location.href = "/login";
  }, 2000);
}

/**
 * Drop-in replacement for window.fetch that:
 *  1. Validates the access token before each request.
 *  2. Merges the Authorization header automatically.
 *  3. Intercepts 401 responses and redirects to /login.
 *
 * Services still parse the Response body themselves — this wrapper
 * only adds auth and 401 handling on top of native fetch.
 *
 * For FormData bodies the Content-Type header is intentionally omitted
 * so the browser can set the multipart boundary.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // If the user is already idle-timed-out, do NOT mark activity first.
  // Otherwise a request made after a long idle would "revive" the session.
  if (isIdleTimedOut()) {
    handleUnauthorized("idle");
    return new Promise<Response>(() => {});
  }

  const token = getValidAccessToken();
  if (!token) {
    handleUnauthorized("missing_token");
    // Return a never-resolving promise so callers' catch blocks don't fire;
    // the page is redirecting to /login anyway.
    return new Promise<Response>(() => {});
  }

  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${token}`);

  // Do not set Content-Type for FormData — the browser must set the
  // multipart boundary itself.
  if (!(options.body instanceof FormData)) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  // Forward session vertical override as X-Preview-Vertical:
  // - exec/global-admin preview switcher, or
  // - open landing lock (aviation from /aviation login for any role).
  // Strip stale preview-only keys when the user is not eligible for the switcher.
  if (!headers.has("X-Preview-Vertical")) {
    try {
      const override = getRequestVerticalOverride();
      if (override) {
        headers.set("X-Preview-Vertical", override);
      } else if (
        sessionStorage.getItem("unicomply.previewVertical") &&
        !canPreviewOtherVerticals()
      ) {
        sessionStorage.removeItem("unicomply.previewVertical");
      }
    } catch {
      // sessionStorage may be disabled (private browsing); preview is best-effort.
    }
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 402) {
    try {
      const cloned = response.clone();
      const body = (await cloned.json()) as { code?: string };
      if (body?.code === "LICENSE_INACTIVE") {
        window.dispatchEvent(new CustomEvent(RULES_LICENSE_INACTIVE_EVENT));
      }
    } catch {
      window.dispatchEvent(new CustomEvent(RULES_LICENSE_INACTIVE_EVENT));
    }
  }

  if (response.status === 403) {
    try {
      const cloned = response.clone();
      const body = (await cloned.json()) as { code?: string };
      const code = String(body?.code ?? "");
      if (
        code === "ACCESS_REMOVED" ||
        code === "FACILITY_ACCESS_REMOVED" ||
        code === "NO_ACTIVE_FACILITY_ASSOCIATIONS"
      ) {
        window.dispatchEvent(new CustomEvent(FACILITY_ACCESS_REMOVED_EVENT));
      }
    } catch {
      /* non-JSON 403 */
    }
  }

  if (response.status === 401) {
    handleUnauthorized("unauthorized");
    // Only swallow the response if a redirect is actually in progress.
    // When handleUnauthorized returns early (e.g. token still within its valid
    // window — likely a permission issue on a specific endpoint), propagate the
    // 401 back so the individual caller can handle it gracefully.
    if (isRedirecting) {
      return new Promise<Response>(() => {});
    }
    return response;
  }

  return response;
}

/**
 * Lightweight 401 check for services that use OPTIONAL tokens.
 * Call this after a plain `fetch` to trigger logout + redirect when the
 * server says the token is invalid.
 *
 * Usage:
 *   const response = await fetch(url, { headers });
 *   check401(response);          // redirects if 401
 *   // …continue processing…
 */
export function check401(response: Response): void {
  if (response.status === 401) {
    handleUnauthorized("unauthorized");
  }
}
